import React from 'react';
import { ArrowLeft, Calendar, MapPin, TrendingDown, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';

const UPSKuehneArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Industry Highlights
          </Button>
          
          <div className="flex items-center gap-4 mb-4">
            <Badge className="bg-orange-500">
              <TrendingDown className="h-3 w-3 mr-1" />
              {t('article.upskuehne.badge')}
            </Badge>
            <div className="flex items-center text-gray-600">
              <Calendar className="h-4 w-4 mr-2" />
              {t('article.upskuehne.date')}
            </div>
            <div className="flex items-center text-gray-600">
              <MapPin className="h-4 w-4 mr-2" />
              {t('article.upskuehne.location')}
            </div>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('article.upskuehne.title')}
          </h1>
          
          <p className="text-xl text-gray-600 leading-relaxed">
            {t('article.upskuehne.summary')}
          </p>
        </div>

        {/* Featured Image */}
        <div className="mb-8">
          <img 
            src="https://cdn-trans.info/uploads/2018/12/7c66071c5029c9bd2ccd03e399b-1024x770.jpg"
            alt={t('article.upskuehne.imageCaption')}
            className="w-full h-96 object-cover rounded-lg shadow-lg"
          />
          <p className="text-sm text-gray-500 mt-2 text-center">
            {t('article.upskuehne.imageCaption')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Highlight Box */}
            <Card className="border-l-4 border-l-orange-500 bg-orange-50">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <DollarSign className="h-6 w-6 text-orange-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-orange-800 mb-2">Key Highlight</h3>
                    <p className="text-orange-700">{t('article.upskuehne.highlight')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Article Sections */}
            <div className="prose prose-lg max-w-none">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.upskuehne.section1.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.upskuehne.section1.content')}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.upskuehne.section2.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.upskuehne.section2.content')}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.upskuehne.section3.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.upskuehne.section3.content')}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.upskuehne.section4.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.upskuehne.section4.content')}
                </p>
              </div>
            </div>

            {/* Impact Analysis */}
            <Card className="bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200">
              <CardHeader>
                <CardTitle className="flex items-center text-orange-800">
                  <DollarSign className="h-5 w-5 mr-2" />
                  {t('article.upskuehne.impact.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-orange-700">
                  {t('article.upskuehne.impact.content')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Key Metrics */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Key Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Companies</span>
                  <span className="font-semibold">UPS & Kuehne+Nagel</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Focus</span>
                  <span className="font-semibold text-orange-600">Cost Reduction</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Market</span>
                  <span className="font-semibold">Challenging</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Status</span>
                  <Badge className="bg-orange-500">Cost Management</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Source */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Source</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  LinkedIn - Ti Week in Logistics
                </p>
                <Button 
                  className="w-full" 
                  onClick={() => window.open('https://www.linkedin.com/posts/ti-week-in-logistics_ups-and-kuehne-nagel-reduce-costs-amid-challenging-market-conditions', '_blank')}
                >
                  Read Original Article
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UPSKuehneArticle;